import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { inventoryItemSchema, type InventoryItemFormData } from '../../types/inventory';
import { inventoryService } from '../../services/inventoryService';
import { greenBeanService } from '../../services/greenBeanService';
import { useSettingsStore } from '../../store/settingsStore';
import { useWeightUnit } from '../useWeightUnit';
import type { CreateGreenBeanProfileDTO } from '../../types/greenBean';
import { useLanguage } from '../../features/language/useLanguage';

export const useCreateInventoryPageState = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const queryClient = useQueryClient();
  const { defaultUnit } = useSettingsStore();
  const { unit, toggleUnit } = useWeightUnit(defaultUnit as 'kg' | 'gram');

  const methods = useForm<InventoryItemFormData>({
    resolver: zodResolver(inventoryItemSchema) as never,
    defaultValues: {
      reorderPoint: 0,
      reorderQuantity: 0,
      standardCost: 0,
      itemType: 'green_bean',
      quantity: 0,
      unitOfMeasure: defaultUnit,
      green_bean_profile: {
        moisture_content: 0,
        harvest_date: new Date().getFullYear().toString(),
        aroma: [],
        sample_physical_check: null,
        country: '',
        region: '',
        farm_name: '',
        producer: '',
        species: '',
        variety: '',
        processor: '',
        wet_mill: '',
        dry_mill: '',
        processing_other: '',
        processing_notes: '',
        origin_notes: '',
        origin_details: '',
        taste_profile: '',
        color: '',
        grade: '',
        profile_code: '',
        profile_name: '',
        lot_id: '',
        roast_id: '',
        process_type: 'Other',
        qc_cupping: '',
      } as unknown as CreateGreenBeanProfileDTO,
    },
  });

  const { register, handleSubmit, reset, setValue, control, formState } = methods;
  const itemType = useWatch({ control, name: 'itemType' });
  const standardCost = useWatch({ control, name: 'standardCost' });
  const unitOfMeasure = useWatch({ control, name: 'unitOfMeasure' });
  const isGreenBean = itemType === 'green_bean';
  const isWeightBased = ['kg', 'g', 'lb', 'oz'].includes(unitOfMeasure);

  const { data: allProfilesData } = useQuery({
    queryKey: ['green-beans'],
    queryFn: greenBeanService.getAll,
    staleTime: 5 * 60 * 1000,
    enabled: isGreenBean,
  });

  const allProfiles = useMemo(() => allProfilesData || [], [allProfilesData]);

  const { data: item, isLoading: isLoadingItem } = useQuery({
    queryKey: ['inventory', id],
    queryFn: () => inventoryService.getById(id!),
    enabled: isEditMode,
  });

  const { data: selectedProfile } = useQuery({
    queryKey: ['green-bean', item?.greenBeanProfileId],
    queryFn: () => greenBeanService.getById(item!.greenBeanProfileId!),
    enabled: !!item?.greenBeanProfileId,
  });

  useEffect(() => {
    if (item) {
      reset({
        ...item,
        reorderPoint: Number(item.reorderPoint),
        reorderQuantity: Number(item.reorderQuantity),
        standardCost: Number(item.standardCost),
        quantity: Number(item.quantity || item.stock),
        green_bean_profile: {
          country: '',
          region: '',
          farm_name: '',
          producer: '',
          species: '',
          variety: '',
          processor: '',
          wet_mill: '',
          dry_mill: '',
          processing_other: '',
          processing_notes: '',
          origin_notes: '',
          origin_details: '',
          taste_profile: '',
          color: '',
          grade: '',
          profile_code: '',
          profile_name: '',
          lot_id: '',
          roast_id: '',
          moisture_content: 0,
          harvest_date: new Date().getFullYear().toString(),
          aroma: [],
          sample_physical_check: null,
          process_type: 'Other',
          qc_cupping: '',
        },
      });
    }
  }, [item, reset]);

  useEffect(() => {
    if (selectedProfile) {
      setValue('green_bean_profile', {
        ...selectedProfile,
        country: selectedProfile.country || '',
        region: selectedProfile.region || '',
        farm_name: selectedProfile.farm_name || '',
        producer: selectedProfile.producer || '',
        species: selectedProfile.species || '',
        variety: selectedProfile.variety || '',
        processor: selectedProfile.processor || '',
        wet_mill: selectedProfile.wet_mill || '',
        dry_mill: selectedProfile.dry_mill || '',
        processing_other: selectedProfile.processing_other || '',
        processing_notes: selectedProfile.processing_notes || '',
        origin_notes: selectedProfile.origin_notes || '',
        origin_details: selectedProfile.origin_details || '',
        taste_profile: selectedProfile.taste_profile || '',
        lot_id: selectedProfile.lot_id || '',
        roast_id: selectedProfile.roast_id || '',
        color: selectedProfile.color || '',
        grade: selectedProfile.grade ? String(selectedProfile.grade) : '',
        moisture_content: Number(selectedProfile.moisture_content || 0),
        sample_physical_check: selectedProfile.sample_physical_check ?? null,
        aroma: Array.isArray(selectedProfile.aroma) ? selectedProfile.aroma : [],
        profile_code: selectedProfile.profile_code || '',
        profile_name: selectedProfile.profile_name || '',
        process_type: selectedProfile.process_type || 'Other',
        qc_cupping: selectedProfile.qc_cupping || '',
        harvest_date: selectedProfile.harvest_date || new Date().getFullYear().toString(),
      });
    }
  }, [selectedProfile, setValue]);

  const uniqueOptions = useMemo(() => {
    const extract = (key: keyof CreateGreenBeanProfileDTO) =>
      Array.from(new Set(allProfiles.map(profile => profile[key] as string).filter(Boolean))).sort();

    return {
      regions: extract('region'),
      farms: extract('farm_name'),
      producers: extract('producer'),
      species: extract('species'),
      varieties: extract('variety'),
      processors: extract('processor'),
    };
  }, [allProfiles]);

  const onSubmit = async (data: InventoryItemFormData) => {
    try {
      const payload = { ...data };
      if (!isGreenBean) {
        delete payload.green_bean_profile;
        delete payload.greenBeanProfileId;
        delete payload.newGreenBeanProfile;
      } else {
        delete payload.greenBeanProfileId;
        if (isEditMode && selectedProfile) {
          const formP = payload.green_bean_profile;
          const selP = {
            profile_code: selectedProfile.profile_code || '',
            profile_name: selectedProfile.profile_name || '',
            moisture_content: Number(selectedProfile.moisture_content || 0),
            color: selectedProfile.color || '',
            grade: selectedProfile.grade ? String(selectedProfile.grade) : '',
            sample_physical_check: selectedProfile.sample_physical_check ?? null,
            aroma: Array.isArray(selectedProfile.aroma) ? selectedProfile.aroma : [],
            country: selectedProfile.country || '',
            region: selectedProfile.region || '',
            farm_name: selectedProfile.farm_name || '',
            producer: selectedProfile.producer || '',
            species: selectedProfile.species || '',
            variety: selectedProfile.variety || '',
            harvest_date: selectedProfile.harvest_date || '',
            origin_notes: selectedProfile.origin_notes || '',
            processor: selectedProfile.processor || '',
            wet_mill: selectedProfile.wet_mill || '',
            dry_mill: selectedProfile.dry_mill || '',
            processing_other: selectedProfile.processing_other || '',
            process_type: selectedProfile.process_type || '',
            processing_notes: selectedProfile.processing_notes || '',
            lot_id: selectedProfile.lot_id || '',
            roast_id: selectedProfile.roast_id || '',
            origin_details: selectedProfile.origin_details || '',
            taste_profile: selectedProfile.taste_profile || '',
            qc_cupping: selectedProfile.qc_cupping || '',
          };

          const arraysEqual = (a?: string[], b?: string[]) => {
            const aa = Array.isArray(a) ? a : [];
            const bb = Array.isArray(b) ? b : [];
            if (aa.length !== bb.length) return false;
            for (let i = 0; i < aa.length; i++) {
              if (aa[i] !== bb[i]) return false;
            }
            return true;
          };

          const equal =
            (formP?.profile_code || '') === selP.profile_code &&
            (formP?.profile_name || '') === selP.profile_name &&
            Number(formP?.moisture_content || 0) === selP.moisture_content &&
            (formP?.color || '') === selP.color &&
            (formP?.grade ? String(formP.grade) : '') === selP.grade &&
            (formP?.sample_physical_check ?? null) === selP.sample_physical_check &&
            arraysEqual(formP?.aroma, selP.aroma) &&
            (formP?.country || '') === selP.country &&
            (formP?.region || '') === selP.region &&
            (formP?.farm_name || '') === selP.farm_name &&
            (formP?.producer || '') === selP.producer &&
            (formP?.species || '') === selP.species &&
            (formP?.variety || '') === selP.variety &&
            (formP?.harvest_date || '') === selP.harvest_date &&
            (formP?.origin_notes || '') === selP.origin_notes &&
            (formP?.processor || '') === selP.processor &&
            (formP?.wet_mill || '') === selP.wet_mill &&
            (formP?.dry_mill || '') === selP.dry_mill &&
            (formP?.processing_other || '') === selP.processing_other &&
            (formP?.process_type || '') === selP.process_type &&
            (formP?.processing_notes || '') === selP.processing_notes &&
            (formP?.lot_id || '') === selP.lot_id &&
            (formP?.roast_id || '') === selP.roast_id &&
            (formP?.origin_details || '') === selP.origin_details &&
            (formP?.taste_profile || '') === selP.taste_profile &&
            (formP?.qc_cupping || '') === selP.qc_cupping;

          if (equal) {
            delete payload.green_bean_profile;
          }
        }
      }

      if (isEditMode && id) {
        await inventoryService.update(id, payload);
      } else {
        await inventoryService.create(payload);
      }

      await queryClient.invalidateQueries({ queryKey: ['inventory'] });
      navigate('/inventory');
    } catch (error) {
      console.error('Failed to save inventory item:', error);
      alert(t('inventory.modal.errorSave', { message: t('errors.common.unknownError') }));
    }
  };

  return {
    navigate,
    isEditMode,
    isLoadingItem,
    methods,
    register,
    handleSubmit,
    setValue,
    control,
    errors: formState.errors,
    isSubmitting: formState.isSubmitting,
    itemType,
    standardCost: standardCost ?? 0,
    unitOfMeasure,
    isGreenBean,
    isWeightBased,
    unit,
    toggleUnit,
    uniqueOptions,
    onSubmit,
  };
};
